// RefundPaymentModal — end-to-end UI for the payment.refund action.
//
// Architecture: frontend is a pure proxy. The button only renders for
// `payment_type === 'received'` payments AND only when the Action Engine
// says the user is allowed via `useCan('payment.refund', payment)`.
// The modal posts to /payments/{id}/refund which is gated, audit-tagged,
// and idempotency-protected on the server.
import React, { useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { X, Undo2, AlertTriangle } from 'lucide-react';

const _moneyFmt = new Intl.NumberFormat('ar-SA', {
  style: 'currency', currency: 'SAR', maximumFractionDigits: 2,
});
const money = (n) => _moneyFmt.format(Number(n || 0));

export default function RefundPaymentModal({ payment, onClose, onRefunded }) {
  const maxAmount = useMemo(() => Number(payment?.amount || 0), [payment]);
  const [amount, setAmount] = useState(String(maxAmount));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const numericAmount = Number(amount);
  const isValid = numericAmount > 0 && numericAmount <= maxAmount;

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error(`المبلغ يجب أن يكون بين 0 و ${money(maxAmount)}`);
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance.post(`/payments/${payment.id}/refund`, {
        amount: numericAmount,
        reason: reason || null,
      });
      const remaining = res.data?.remaining_after ?? 0;
      toast.success(
        remaining > 0
          ? `تم استرداد ${money(numericAmount)} (متبقٍ ${money(remaining)})`
          : `تم استرداد ${money(numericAmount)} — تم استرداد الدفعة بالكامل`
      );
      onRefunded && onRefunded(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذّر الاسترداد');
    } finally {
      setSaving(false);
    }
  };

  if (!payment) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      dir="rtl"
      data-testid="refund-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-rose-50 text-rose-600">
              <Undo2 size={18} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                استرداد دفعة
              </div>
              <div className="text-lg font-semibold text-slate-900">
                {payment.payment_number}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                المبلغ الأصلي: <span className="font-medium">{money(maxAmount)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div className="flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              عملية حساسة — تُسجَّل في سجل التدقيق كـ
              <span className="font-mono mx-1">FINANCIAL_REVERSAL</span>
              ولا يمكن التراجع عنها بسهولة.
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              مبلغ الاسترداد (SAR)
            </label>
            <input
              type="number"
              min="0"
              max={maxAmount}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="refund-amount"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            {!isValid && amount !== '' && (
              <div className="mt-1 text-xs text-rose-600">
                المبلغ يجب أن يكون أكبر من 0 وأقل أو يساوي {money(maxAmount)}
              </div>
            )}
            <div className="mt-1 flex gap-2 text-xs">
              <button
                type="button"
                onClick={() => setAmount(String(maxAmount))}
                className="text-violet-600 hover:underline"
              >
                استرداد كامل
              </button>
              <button
                type="button"
                onClick={() => setAmount(String((maxAmount / 2).toFixed(2)))}
                className="text-violet-600 hover:underline"
              >
                نصف المبلغ
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              سبب الاسترداد (للتدقيق)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="refund-reason"
              placeholder="مثلاً: إرجاع منتج / إلغاء عقد"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm"
          >
            إلغاء
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !isValid}
            data-testid="refund-submit"
            className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Undo2 size={14} />
            {saving ? 'جاري الاسترداد…' : 'تأكيد الاسترداد'}
          </button>
        </div>
      </div>
    </div>
  );
}
